import React, { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { toast } from 'sonner';
import { api } from '../../services/api.js';
import { extractApiError } from '../../utils/pdfDownload';
import { Plus, Edit2, Trash2, Star, Package, X } from 'lucide-react';
import { VariantImageUpload } from './VariantImageUpload';
import { VariantLabelFields } from './VariantLabelFields';

interface Variant {
  id: string;
  product_id: string;
  gebinde_name: string;
  units_per_case: number;
  unit_volume?: string;
  pfand_per_unit: number;
  pfand_per_case: number;
  price_net: number;
  weight_kg?: number;
  is_default: boolean;
  is_active: boolean;
  /** Flavour without the pack size, e.g. "Sicilian Lemon" — used in emails. */
  flavor_label?: string | null;
  /** Pack size without the flavour, e.g. "24 x 0,33 l Dose". */
  package_label?: string | null;
  image_path?: string | null;
  sales_designation?: string | null;
  ean?: string | null;
  leh_unit_price_net?: number | string | null;
}

interface VariantForm {
  gebinde_name: string;
  units_per_case: number;
  unit_volume: string;
  pfand_per_unit: number;
  pfand_per_case: number;
  /** Empty string until the user types — `0` would look like a blank required field. */
  price_net: string;
  weight_kg: string;
  is_default: boolean;
  flavor_label: string;
  package_label: string;
  sales_designation: string;
  ean: string;
  leh_unit_price_net: string;
}

const EMPTY_FORM: VariantForm = {
  gebinde_name: '',
  units_per_case: 24,
  unit_volume: '',
  pfand_per_unit: 0.08,
  pfand_per_case: 0,
  price_net: '',
  weight_kg: '',
  is_default: false,
  flavor_label: '',
  package_label: '',
  sales_designation: '',
  ean: '',
  leh_unit_price_net: '',
};

interface VariantPanelProps {
  productId: string;
  productName: string;
  /** Lets the product list refresh its email readiness badge after a change here. */
  onVariantsChanged?: () => void;
}

export const VariantPanel: React.FC<VariantPanelProps> = ({
  productId,
  productName,
  onVariantsChanged,
}) => {
  const [variants, setVariants] = useState<Variant[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Variant | null>(null);
  const [form, setForm] = useState<VariantForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const fetchVariants = async () => {
    try {
      const res = await api.get(`/products/${productId}/variants`);
      setVariants(res.data);
    } catch {
      toast.error('Failed to load variants');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVariants();
  }, [productId]);

  const openForm = (variant?: Variant) => {
    if (variant) {
      setEditing(variant);
      // Postgres DECIMAL columns are returned as strings by node-pg.
      setForm({
        gebinde_name: variant.gebinde_name,
        units_per_case: Number(variant.units_per_case),
        unit_volume: variant.unit_volume || '',
        pfand_per_unit: Number(variant.pfand_per_unit) || 0,
        pfand_per_case: Number(variant.pfand_per_case) || 0,
        price_net: String(Number(variant.price_net) || 0),
        weight_kg: variant.weight_kg != null ? String(variant.weight_kg) : '',
        is_default: variant.is_default,
        flavor_label: variant.flavor_label || '',
        package_label: variant.package_label || '',
        sales_designation: variant.sales_designation || '',
        ean: variant.ean || '',
        leh_unit_price_net:
          variant.leh_unit_price_net != null && variant.leh_unit_price_net !== ''
            ? String(Number(variant.leh_unit_price_net))
            : '',
      });
    } else {
      setEditing(null);
      setForm(EMPTY_FORM);
    }
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const priceNet = parseFloat(form.price_net);
    if (!Number.isFinite(priceNet) || priceNet <= 0) {
      toast.error('Price net must be greater than 0');
      return;
    }
    const lehRaw = form.leh_unit_price_net.trim();
    const lehNet = lehRaw === '' ? null : parseFloat(lehRaw);
    if (lehNet !== null && (!Number.isFinite(lehNet) || lehNet <= 0)) {
      toast.error('LEH price must be greater than 0, or left empty');
      return;
    }
    setSaving(true);
    const payload = {
      gebinde_name: form.gebinde_name,
      units_per_case: form.units_per_case,
      pfand_per_unit: form.pfand_per_unit,
      pfand_per_case: form.pfand_per_case,
      price_net: priceNet,
      unit_volume: form.unit_volume || undefined,
      weight_kg: form.weight_kg ? parseFloat(form.weight_kg) : undefined,
      is_default: form.is_default,
      flavor_label: form.flavor_label.trim() || null,
      package_label: form.package_label.trim() || null,
      sales_designation: form.sales_designation.trim() || null,
      ean: form.ean.trim() || null,
      leh_unit_price_net: lehNet,
    };
    try {
      if (editing) {
        await api.put(`/products/variants/${editing.id}`, payload);
        toast.success('Variant updated');
      } else {
        await api.post(`/products/${productId}/variants`, payload);
        toast.success('Variant added');
      }
      closeForm();
      fetchVariants();
      onVariantsChanged?.();
    } catch (err) {
      const { message } = extractApiError(err);
      toast.error(`Failed to save variant: ${message}`);
    } finally {
      setSaving(false);
    }
  };

  const updateVariantImage = (variantId: string, imagePath: string | null) => {
    setVariants((prev) =>
      prev.map((v) => (v.id === variantId ? { ...v, image_path: imagePath } : v))
    );
    onVariantsChanged?.();
  };

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/products/variants/${id}`);
      toast.success('Variant deleted');
      fetchVariants();
      onVariantsChanged?.();
    } catch (err) {
      const { message } = extractApiError(err);
      toast.error(`Failed to delete variant: ${message}`);
    }
  };

  if (loading) {
    return (
      <div className="p-4 text-center text-gray-400 text-xs font-bold">
        Loading variants...
      </div>
    );
  }

  return (
    <div className="bg-gray-50 border-t-2 border-gray-100 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-black text-gray-500 uppercase tracking-widest">
          Gebinde / Variants
        </h4>
        <button
          type="button"
          onClick={() => openForm()}
          className="flex items-center gap-1 text-xs font-bold text-brand-600 hover:text-brand-700 transition-colors"
        >
          <Plus size={14} /> Add
        </button>
      </div>

      {variants.length === 0 ? (
        <div className="text-center py-6 text-gray-400">
          <Package size={24} className="mx-auto mb-2 opacity-50" />
          <p className="text-xs font-bold">No variants yet</p>
        </div>
      ) : (
        <div className="space-y-2">
          {variants.map((v) => (
            <div
              key={v.id}
              className={`p-3 rounded-xl border-2 ${
                v.is_default
                  ? 'border-brand-200 bg-brand-50/50'
                  : 'border-gray-200 bg-white'
              } ${!v.is_active ? 'opacity-50' : ''}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-sm text-gray-900">
                      {v.gebinde_name}
                    </span>
                    {v.is_default && (
                      <Star
                        size={12}
                        className="text-brand-500 fill-brand-500"
                      />
                    )}
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-gray-500">
                    <span>{v.units_per_case} units/case</span>
                    {v.unit_volume && <span>{v.unit_volume}</span>}
                    <span className="font-bold text-gray-700">
                      {Number(v.price_net).toFixed(2)} net
                    </span>
                    {v.leh_unit_price_net != null && v.leh_unit_price_net !== '' && (
                      <span>LEH {Number(v.leh_unit_price_net).toFixed(2)}/unit</span>
                    )}
                    <span>
                      Pfand: {Number(v.pfand_per_case).toFixed(2)}/case
                    </span>
                    {v.weight_kg && <span>{Number(v.weight_kg).toFixed(1)} kg</span>}
                  </div>
                  {(v.flavor_label || v.package_label) && (
                    <p className="mt-1 text-xs text-gray-400">
                      {[v.flavor_label, v.package_label].filter(Boolean).join(' · ')}
                    </p>
                  )}
                </div>
                <div className="flex gap-1 ml-2">
                  <button
                    type="button"
                    onClick={() => openForm(v)}
                    className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-brand-600 transition-colors"
                  >
                    <Edit2 size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleDelete(v.id)}
                    className="p-1.5 hover:bg-red-50 rounded-lg text-gray-400 hover:text-red-600 transition-colors"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-gray-100">
                <VariantImageUpload
                  variantId={v.id}
                  imagePath={v.image_path ?? null}
                  onChange={(imagePath) => updateVariantImage(v.id, imagePath)}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog.Root
        open={formOpen}
        onOpenChange={(open) => {
          if (!open) closeForm();
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 animate-in fade-in duration-200" />
          <Dialog.Content
            className="fixed left-1/2 top-1/2 z-50 w-[calc(100%-2rem)] max-w-lg max-h-[90vh]
              -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-2xl bg-white p-5 shadow-2xl
              animate-in zoom-in-95 fade-in duration-200 focus:outline-none"
            onOpenAutoFocus={(e) => {
              // Keep focus in the dialog without forcing scroll on the page behind it.
              e.preventDefault();
              const root = e.currentTarget as HTMLElement;
              root.querySelector<HTMLInputElement>('input')?.focus();
            }}
          >
            <div className="flex items-start justify-between gap-3 mb-4">
              <Dialog.Title className="text-sm font-black text-gray-900 uppercase tracking-wider">
                {editing ? 'Edit' : 'New'} Variant — {productName}
              </Dialog.Title>
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
              </Dialog.Close>
            </div>
            <Dialog.Description className="sr-only">
              {editing
                ? `Edit packaging variant for ${productName}`
                : `Add a packaging variant for ${productName}`}
            </Dialog.Description>

            <form onSubmit={(e) => void handleSubmit(e)} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="text-xs font-bold text-gray-500 mb-1 block">
                    Gebinde Name
                  </label>
                  <input
                    required
                    value={form.gebinde_name}
                    onChange={(e) =>
                      setForm({ ...form, gebinde_name: e.target.value })
                    }
                    placeholder="e.g. 24x0.33L"
                    className="w-full p-2.5 border-2 border-gray-200 rounded-xl text-sm font-bold focus:border-brand-500"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-500 mb-1 block">
                    Units/Case
                  </label>
                  <input
                    type="number"
                    required
                    min={1}
                    value={form.units_per_case}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        units_per_case: parseInt(e.target.value) || 1,
                      })
                    }
                    className="w-full p-2.5 border-2 border-gray-200 rounded-xl text-sm font-bold focus:border-brand-500"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-500 mb-1 block">
                    Unit Volume
                  </label>
                  <input
                    value={form.unit_volume}
                    onChange={(e) =>
                      setForm({ ...form, unit_volume: e.target.value })
                    }
                    placeholder="e.g. 0.33L"
                    className="w-full p-2.5 border-2 border-gray-200 rounded-xl text-sm font-bold focus:border-brand-500"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-500 mb-1 block">
                    Price Net (per case)
                  </label>
                  <input
                    type="number"
                    required
                    min={0.01}
                    step={0.01}
                    value={form.price_net}
                    onChange={(e) =>
                      setForm({ ...form, price_net: e.target.value })
                    }
                    placeholder="e.g. 22.80"
                    className="w-full p-2.5 border-2 border-gray-200 rounded-xl text-sm font-bold focus:border-brand-500"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-500 mb-1 block">
                    Pfand/Unit
                  </label>
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    value={form.pfand_per_unit}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        pfand_per_unit: parseFloat(e.target.value) || 0,
                      })
                    }
                    className="w-full p-2.5 border-2 border-gray-200 rounded-xl text-sm font-bold focus:border-brand-500"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-500 mb-1 block">
                    Pfand/Case
                  </label>
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    value={form.pfand_per_case}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        pfand_per_case: parseFloat(e.target.value) || 0,
                      })
                    }
                    className="w-full p-2.5 border-2 border-gray-200 rounded-xl text-sm font-bold focus:border-brand-500"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-500 mb-1 block">
                    Weight (kg)
                  </label>
                  <input
                    type="number"
                    min={0}
                    step={0.1}
                    value={form.weight_kg}
                    onChange={(e) =>
                      setForm({ ...form, weight_kg: e.target.value })
                    }
                    placeholder="Optional"
                    className="w-full p-2.5 border-2 border-gray-200 rounded-xl text-sm font-bold focus:border-brand-500"
                  />
                </div>
                <VariantLabelFields
                  flavorLabel={form.flavor_label}
                  packageLabel={form.package_label}
                  salesDesignation={form.sales_designation}
                  ean={form.ean}
                  lehUnitPrice={form.leh_unit_price_net}
                  onChange={(patch) => setForm({ ...form, ...patch })}
                />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_default}
                  onChange={(e) =>
                    setForm({ ...form, is_default: e.target.checked })
                  }
                  className="w-4 h-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                />
                <span className="text-xs font-bold text-gray-600">
                  Default variant
                </span>
              </label>
              <div className="flex gap-2 pt-1">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 py-2.5 bg-brand-600 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-brand-700 disabled:opacity-50 transition-all"
                >
                  {saving ? 'Saving...' : editing ? 'Update' : 'Add Variant'}
                </button>
                <button
                  type="button"
                  onClick={closeForm}
                  className="px-4 py-2.5 border-2 border-gray-200 rounded-xl text-xs font-bold text-gray-600 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
};
